import { Injectable } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from '../../services/config.service';

import { UITheme, UIThemeVariable } from '../theme.interface';

@Injectable({
	providedIn: 'root'
})
export class ThemeBuilderService {
	
	themeEvent: EventEmitter<{ eventType: string, data?: any, timestamp: Date }> = new EventEmitter();
	
	private themeStyleMap: Map<string, HTMLStyleElement> = new Map();
	
	isOpen: boolean = false;

	constructor(
		private configService: ConfigService,
		private http: HttpClient
	) { }

	open(): void {
		this.isOpen = true;
	}

	async getThemes(page: number = 1, paging: any = {}, constraints: any = {}): Promise<any> {
		let url = this.configService.getConfig().theme.themesURL;
		url += `?page=${page}`;
		url += `&size=${paging.elementsPerPage}`;
		url += `&sort=${paging.sorting.sort}`;
		url += `&constraints=${encodeURIComponent(JSON.stringify(constraints))}`;

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async createTheme(payload: UITheme): Promise<any> {
		let url = this.configService.getConfig().theme.themesURL;
		url += `add`;

		try {
			let theme = await firstValueFrom(this.http.post<any>(url, payload));
			
			this.themeEvent.emit({
				eventType: 'themeCreated',
				data: { theme: theme },
				timestamp: new Date(),
			});	
			
			return theme;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async updateTheme(id: number, payload: UITheme): Promise<any> {
		let url = this.configService.getConfig().theme.themesURL;
		url += `update/${id}`;

		try {
			let theme = await firstValueFrom(this.http.put<any>(url, payload));
			
			this.themeEvent.emit({
				eventType: 'themeUpdated',
				data: { theme: theme },
				timestamp: new Date(),
			});	
			
			return theme;		
		} catch (error) {
			return Promise.reject(error);
		}
	}

	getThemeAttribute(): string {
		let themeName = document.documentElement.getAttribute('theme') || '';
		return themeName;
	}

	setThemeAttribute(themeName: string): void {
		document.documentElement.setAttribute('theme', themeName);
	}
	
	async getEnabledThemes(): Promise<any> {
		let constraints: any = { 'enabled': true };
		let pagination = {
			page: 1,
			elementsPerPage: 50,
			sorting: {
				sort: 'created,asc',
			}
		};

		let response = await this.getThemes(0, pagination, constraints);
		let themes = response?.content ?? [];

		return themes;
	}	

	async applyThemes(): Promise<any> {
		let themes = await this.getEnabledThemes();

		themes.forEach((theme: UITheme) => this.injectThemeStyle(theme));
	}

	private injectThemeStyle(theme: UITheme): void {
		this.removeThemeStyle(theme.name);
		
		const styleId = `theme-style-${theme.name}`;
		const style = document.createElement('style');
		style.id = styleId;
		style.textContent = this.generateThemeCSS(theme);

		document.head.appendChild(style);

		this.themeStyleMap.set(theme.name, style);
	}

	private generateThemeCSS(theme: UITheme): string {
		const lines = theme.variables.map((v: UIThemeVariable) => `  ${v.name}: ${this.quotify(v)};`);
		return `html[theme="${theme.name}"] {\n${lines.join('\n')}\n}`;
	}

	private quotify(v: UIThemeVariable): string {
		return v.type === 'copy'
			? `"${v.value.replace(/"/g, '\\"')}"`
			: v.value;
	}

	removeThemeStyle(themeName: string): void {
		const style = this.themeStyleMap.get(themeName);
		if (style) {
			style.remove();
			this.themeStyleMap.delete(themeName);
		}
	}
	
	disableThemes(): void {
		this.themeStyleMap.forEach((styleElement: HTMLStyleElement, themeName: string) => {
		  styleElement.disabled = true;
		});
	}
	
	enableThemes(): void {
		this.themeStyleMap.forEach((styleElement: HTMLStyleElement, themeName: string) => {
		  styleElement.disabled = false;
		});
	}
}
